/** React bindings for Trivelta Product catalog checks (TCM V2). */
import { useState, useEffect } from "react";

import featureFlagService from "../services/FeatureFlagService";
import type { TriveltaProductKey } from "../utils/products";

export interface UseTriveltaProductResult {
  isActive: boolean | null;
  loading: boolean;
  error: Error | null;
}

export interface UseTriveltaProductOptions {
  /**
   * Catalog presence only — skips `isDevModeEnv` and the PAMENG-1820
   * fail-open / legacy-flag fallback in `isProductActive`. Use for products
   * that were never v1-flag-gated (e.g. predictions).
   */
  catalogOnly?: boolean;
}

/**
 * `isActive` is `null` while loading AND on error — check `error` before
 * treating `null` as "still loading". We intentionally don't collapse an
 * outage into `false`; that would silently hide UI whenever v2 is down.
 *
 * TODO(PAMENG-1820): drop `legacyFallbackFeature` when the v2 rollout
 * completes — see `FeatureFlagService.isProductActive`.
 */
export function useTriveltaProduct(
  productKey: TriveltaProductKey,
  legacyFallbackFeature?: string,
  options?: UseTriveltaProductOptions,
): UseTriveltaProductResult {
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const catalogOnly = Boolean(options?.catalogOnly);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setIsActive(null);
        await featureFlagService.initialize();
        if (cancelled) return;
        setIsActive(
          catalogOnly
            ? featureFlagService.isProductKeyActive(productKey)
            : featureFlagService.isProductActive(
                productKey,
                legacyFallbackFeature,
              ),
        );
      } catch (err) {
        if (cancelled) return;
        setError(err as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productKey, legacyFallbackFeature, catalogOnly]);

  return { isActive, loading, error };
}

/**
 * Synchronous variant for use outside React. Returns `false` before
 * `FeatureFlagService.initialize()` resolves.
 *
 * TODO(PAMENG-1820): drop `legacyFallbackFeature` — see `useTriveltaProduct`.
 */
export function checkTriveltaProduct(
  productKey: TriveltaProductKey,
  legacyFallbackFeature?: string,
  options?: UseTriveltaProductOptions,
): boolean {
  if (options?.catalogOnly) {
    return featureFlagService.isProductKeyActive(productKey);
  }
  return featureFlagService.isProductActive(productKey, legacyFallbackFeature);
}
